import { getHost } from './services/endpoints';
import { EventQuery } from './types';

export const navigateToEvent = (externalId: string) => {
  window.open(
    getHost() + `/organisers/events/${externalId}/overview`,
    '_blank',
  );
};

export const formatDate = (value: string): string => {
  const date = new Date(value);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateForFilter = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getDateRange = (selectedValue: string) => {
  const date = new Date();
  switch (selectedValue) {
    case 'last-30-days':
      date.setDate(date.getDate() - 30);
      return formatDateForFilter(date);

    case 'last-3-months':
      date.setMonth(date.getMonth() - 3);
      return formatDateForFilter(date);

    case 'last-6-months':
      date.setMonth(date.getMonth() - 6);
      return formatDateForFilter(date);

    case 'last-year':
      date.setFullYear(date.getFullYear() - 1);
      return formatDateForFilter(date);
  }
  return '';
};

export const buildQuery = (config: EventQuery) => {
  const params = new URLSearchParams();

  if (config.searchText) params.append('searchText', config.searchText);
  if (config.name && config.direction) {
    params.append('sortKey', config.name);
    params.append('reverse', (config.direction === 'desc').toString());
  }
  if (config.dateRange === 'all-time') {
    if (config.endTime) {
      params.append('startTime', '2020-01-01');
      params.append('endTime', config.endTime);
    } else {
      params.append('from', '2020-01-01');
    }
  } else {
    if (config.dateRange && !config.endTime) {
      if (config.dateRange === 'custom') {
        if (config.startDate) params.append('from', config.startDate);
        if (config.endDate) params.append('to', config.endDate);
      } else {
        params.append('from', getDateRange(config.dateRange));
      }
    }

    if (config.endTime) {
      params.append(
        'startTime',
        config.dateRange === 'custom'
          ? config.startTime || ''
          : getDateRange(config.startTime || ''),
      );
      params.append('endTime', config.endTime);
    }
  }

  if (config.eventSeriesId) {
    params.append('eventSeriesId', config.eventSeriesId);
  }

  if (config.page) {
    params.append('page', config.page);
  }

  if (config.perPage) {
    params.append('perPage', config.perPage);
  }

  if (config.eventTypes) {
    Object.entries(config.eventTypes).forEach(([key, value]) => {
      if (value) params.append('venueTypes[]', key);
    });
  }

  if (config.contactType && config.contactType !== 'all') {
    params.append('contactType', config.contactType);
  }

  if (config.eventNames) {
    config.eventNames.forEach(value => {
      if (value) params.append('eventName[]', value);
    });
  }

  if (
    !config.eventSeriesId &&
    config.selectedEvents &&
    config.selectedEvents.length > 0
  ) {
    config.selectedEvents.forEach(eventId => {
      if (eventId) params.append('eventsFilter[]', eventId);
    });
  }

  if (config.emailId) {
    params.append('registrantEmailId', config.emailId);
  }

  if (config.limit) {
    params.append('limit', config.limit.toString());
  }
  if (config.includeContacts) {
    params.append('includeContacts', 'true');
  }

  // Exclude upcoming events
  params.append(
    'excludeUpcoming',
    config.includeUpcoming || config.eventSeriesId ? 'false' : 'true',
  );

  return params.toString();
};

export const getNextPageCount = (pagination: {
  page: number;
  totalCount: number;
  totalPages: number;
  perPage: number;
}) => {
  if (pagination.page && pagination.page + 1 === pagination.totalPages) {
    return pagination.totalCount - pagination.page * pagination.perPage;
  }
  return pagination?.perPage;
};

export const getTitleCase = (value: string) => {
  if (!value || typeof value !== 'string') {
    return '';
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
};

// Format duration in hours, minutes, and seconds
export const formatDuration = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const parts = [];
  if (hours > 0) {
    parts.push(`${hours} hrs`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} mins`);
  }
  if (remainingSeconds > 0) {
    parts.push(`${remainingSeconds} secs`);
  }

  return parts.join(' ');
};

// Transforms array of objects with snake_case keys to camelCase keys
export const transformToCamelCase = <T extends Record<string, any>>(
  data: T[],
): any[] => {
  return data.map(item => {
    const transformed: Record<string, any> = {};

    Object.keys(item).forEach(key => {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
        letter.toUpperCase(),
      );
      transformed[camelKey] = item[key];
    });

    return transformed;
  });
};

export const minutesToReadableTime = (totalMinutes: number): string => {
  totalMinutes = Math.round(totalMinutes);

  const minutesInDay = 1440;
  const minutesInHour = 60;

  if (totalMinutes < minutesInHour) {
    return `${totalMinutes} min${totalMinutes !== 1 ? 's' : ''}`;
  }

  if (totalMinutes < minutesInDay) {
    const hours = Math.floor(totalMinutes / minutesInHour);
    const mins = totalMinutes % minutesInHour;
    return `${hours} hr${hours !== 1 ? 's' : ''}${
      mins > 0 ? ` ${mins} min${mins !== 1 ? 's' : ''}` : ''
    }`;
  }

  // 1 day or more
  const days = Math.floor(totalMinutes / minutesInDay);
  const remainder = totalMinutes % minutesInDay;
  const hours = Math.floor(remainder / minutesInHour);
  const mins = remainder % minutesInHour;

  let result = `${days} day${days !== 1 ? 's' : ''}`;
  if (hours > 0) result += ` ${hours} hr${hours !== 1 ? 's' : ''}`;
  if (mins > 0) result += ` ${mins} min${mins !== 1 ? 's' : ''}`;

  return result;
};
